/**
 * Scroll Animation Engine
 * Lightweight, data-attribute-driven scroll animations for Shopify sections.
 *
 * Supports:
 *   - data-scroll-animate="fade-up|fade-down|fade-left|fade-right|fade|scale-in|scale-out|clip-reveal|rotate-in"
 *   - data-scroll-delay="short|medium|long"
 *   - data-scroll-mode="once|repeat"  (default: once)
 *   - data-scroll-parallax  data-parallax-speed="0.15"  data-parallax-direction="up|down"
 *   - data-scroll-stagger-children
 *   - data-scroll-text-reveal="words|chars"
 *
 * CSS companion: scroll-animations.css
 */

import {
  prefersReducedMotion,
  isLowPowerDevice,
  debounce,
  requestIdleCallback,
} from '@theme/utilities';

const SELECTORS = [
  '[data-scroll-animate]',
  '[data-scroll-parallax]',
  '[data-scroll-text-reveal]',
  '[data-scroll-stagger-children]',
].join(',');

class ScrollAnimationController {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {Map<HTMLElement, { speed: number, direction: string, inView: boolean }>} */
  #parallaxElements = new Map();

  /** @type {number | null} */
  #rafId = null;

  /** @type {boolean} */
  #ticking = false;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {Set<HTMLElement>} */
  #initialized = new Set();

  constructor() {
    // Global kill switch
    if (typeof Theme !== 'undefined' && Theme.settings && Theme.settings.enableScrollAnimations === false) {
      this.#revealAll();
      return;
    }

    // Accessibility / low-power bailout
    if (prefersReducedMotion() || isLowPowerDevice()) {
      this.#revealAll();
      return;
    }

    this.#createObserver();
    this.#initializeElements(document);
    this.#watchDom();
    this.#bindResize();

    // Shopify theme editor support
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:load', (e) => {
        this.#initializeElements(e.target);
      });
    }
  }

  /* --------------------------------------------------------------------------
     Observer setup
     -------------------------------------------------------------------------- */

  #createObserver() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = /** @type {HTMLElement} */ (entry.target);

          if (entry.isIntersecting) {
            this.#onEnter(el);
          } else {
            this.#onExit(el);
          }
        }
      },
      {
        rootMargin: '0px 0px -10% 0px',
        threshold: [0, 0.1],
      }
    );
  }

  /* --------------------------------------------------------------------------
     Element discovery & initialization
     -------------------------------------------------------------------------- */

  /** @param {Element | Document} root */
  #initializeElements(root) {
    const elements = root.querySelectorAll(SELECTORS);
    for (const el of elements) {
      if (this.#initialized.has(/** @type {HTMLElement} */ (el))) continue;
      this.#initElement(/** @type {HTMLElement} */ (el));
    }

    // Start parallax scroll listener if we have parallax elements
    if (this.#parallaxElements.size > 0 && !this.#ticking) {
      this.#startParallaxListener();
    }
  }

  /** @param {HTMLElement} el */
  #initElement(el) {
    this.#initialized.add(el);

    // Prepare stagger children indices
    if (el.hasAttribute('data-scroll-stagger-children')) {
      const children = el.children;
      for (let i = 0; i < children.length; i++) {
        /** @type {HTMLElement} */ (children[i]).style.setProperty(
          '--scroll-stagger-index',
          String(i)
        );
      }
    }

    // Prepare text reveal (split text into spans before observing)
    if (el.hasAttribute('data-scroll-text-reveal')) {
      this.#splitText(el);
    }

    // Register parallax elements
    if (el.hasAttribute('data-scroll-parallax')) {
      const speed = parseFloat(el.dataset.parallaxSpeed || '0.15');
      const direction = el.dataset.parallaxDirection || 'up';
      this.#parallaxElements.set(el, { speed, direction, inView: false });
    }

    // Observe for viewport intersection
    this.#observer?.observe(el);
  }

  /* --------------------------------------------------------------------------
     Intersection callbacks
     -------------------------------------------------------------------------- */

  /** @param {HTMLElement} el */
  #onEnter(el) {
    // Mark visible — CSS transitions take over
    el.dataset.scrollVisible = 'true';

    // Track parallax viewport state
    const parallax = this.#parallaxElements.get(el);
    if (parallax) {
      parallax.inView = true;
      el.style.willChange = 'transform';
    }

    // Unobserve if mode is "once" (default)
    const mode = el.dataset.scrollMode || 'once';
    if (mode === 'once' && !el.hasAttribute('data-scroll-parallax')) {
      this.#observer?.unobserve(el);
    }
  }

  /** @param {HTMLElement} el */
  #onExit(el) {
    const mode = el.dataset.scrollMode || 'once';

    // For parallax, always track exit
    const parallax = this.#parallaxElements.get(el);
    if (parallax) {
      parallax.inView = false;
      el.style.willChange = '';
    }

    // For repeat mode, hide again on exit
    if (mode === 'repeat') {
      delete el.dataset.scrollVisible;
    }
  }

  /* --------------------------------------------------------------------------
     Parallax scroll handler
     -------------------------------------------------------------------------- */

  #startParallaxListener() {
    const onScroll = () => {
      if (this.#rafId !== null) return;
      this.#rafId = requestAnimationFrame(() => {
        this.#updateParallax();
        this.#rafId = null;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    this.#ticking = true;

    // Initial position
    this.#updateParallax();
  }

  #updateParallax() {
    const viewportCenter = window.innerHeight / 2 + window.scrollY;

    for (const [el, config] of this.#parallaxElements) {
      if (!config.inView) continue;

      const rect = el.getBoundingClientRect();
      const elementCenter = rect.top + rect.height / 2 + window.scrollY;
      const distance = (elementCenter - viewportCenter) * config.speed;
      const direction = config.direction === 'down' ? -1 : 1;

      el.style.transform = `translate3d(0, ${distance * direction}px, 0)`;
    }
  }

  /* --------------------------------------------------------------------------
     Text splitting
     -------------------------------------------------------------------------- */

  /** @param {HTMLElement} el */
  #splitText(el) {
    const mode = el.dataset.scrollTextReveal || 'words';

    // Store original HTML for potential cleanup
    el.dataset.scrollOriginalHtml = el.innerHTML;

    const text = el.textContent || '';
    let html = '';

    if (mode === 'chars') {
      let index = 0;
      for (const char of text) {
        if (char === ' ') {
          html += ' ';
        } else {
          html += `<span class="scroll-anim-char" style="--scroll-word-index: ${index}">${char}</span>`;
          index++;
        }
      }
    } else {
      // Default: words
      const words = text.split(/(\s+)/);
      let index = 0;
      for (const word of words) {
        if (/^\s+$/.test(word)) {
          html += word;
        } else {
          html += `<span class="scroll-anim-word" style="--scroll-word-index: ${index}">${word}</span>`;
          index++;
        }
      }
    }

    el.innerHTML = html;
  }

  /* --------------------------------------------------------------------------
     DOM watcher (Section Rendering API + AJAX content)
     -------------------------------------------------------------------------- */

  #watchDom() {
    const scan = debounce(() => {
      this.#initializeElements(document);
    }, 100);

    this.#mutationObserver = new MutationObserver((mutations) => {
      let hasNewNodes = false;
      for (const mutation of mutations) {
        if (mutation.addedNodes.length > 0) {
          hasNewNodes = true;
          break;
        }
      }
      if (hasNewNodes) scan();
    });

    this.#mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /* --------------------------------------------------------------------------
     Resize handler
     -------------------------------------------------------------------------- */

  #bindResize() {
    // Recalculate parallax on resize (cached positions may be stale)
    window.addEventListener(
      'resize',
      debounce(() => {
        if (this.#parallaxElements.size > 0) {
          this.#updateParallax();
        }
      }, 250)
    );
  }

  /* --------------------------------------------------------------------------
     Bailout: reveal everything immediately
     -------------------------------------------------------------------------- */

  #revealAll() {
    requestIdleCallback(() => {
      const elements = document.querySelectorAll(SELECTORS);
      for (const el of elements) {
        /** @type {HTMLElement} */ (el).dataset.scrollVisible = 'true';
      }
    });
  }
}

// Self-initialize
new ScrollAnimationController();
